import {
  EHeadingStyleVariant,
  EIconButtonVariant,
  GeneralCaretLeftIcon,
  Heading,
  IconButton,
  PageHeader,
  TPageHeaderContentDeviceMode,
  TPageHeaderTopSectionContentPosition,
  useIsWindowWidthGreaterThan,
} from '@outdoorsyco/bonfire';
import Link from 'next/link';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useRouter } from '@/hooks/useRouter';

interface INavigationTabItem {
  key: EAccountPageTab;
  label: string;
  active: boolean;
  badgeCount?: number;
}

interface IAccountNavigation {
  selectedTab: EAccountPageTab;
  setSelectedTab: (tab: EAccountPageTab) => void;
}

export enum EAccountPageTab {
  Profile = 'Profile',
  ProfileEdit = 'Profile-Edit',
  PaymentMethods = 'Payment-Methods',
  Notifications = 'Notifications',
}

export const Navigation = ({ selectedTab, setSelectedTab }: IAccountNavigation) => {
  const intl = useIntl();
  const router = useRouter();

  const desktopView = useIsWindowWidthGreaterThan('sm', true);
  const handleTabSelect = (tab: EAccountPageTab) => {
    router.push(
      {
        pathname: '/account',
        query: { tab },
      },
      undefined,
      {
        shallow: true,
      },
    );

    setSelectedTab(tab);
  };

  const isEditing = selectedTab === EAccountPageTab.ProfileEdit;

  const navTabItems: INavigationTabItem[] = useMemo(() => {
    const allNavigationTabItems = [
      {
        key: EAccountPageTab.Profile,
        label: intl.formatMessage({
          defaultMessage: 'Profile',
          id: 'itPgxd',
        }),
        active:
          selectedTab === EAccountPageTab.Profile || selectedTab === EAccountPageTab.ProfileEdit,
      },
      {
        key: EAccountPageTab.PaymentMethods,
        label: intl.formatMessage({
          defaultMessage: 'Payment methods',
          id: '9k5qQl',
        }),
        active: selectedTab === EAccountPageTab.PaymentMethods,
      },
      {
        key: EAccountPageTab.Notifications,
        label: intl.formatMessage({
          defaultMessage: 'Notifications',
          id: 'NAidKb',
        }),
        active: selectedTab === EAccountPageTab.Notifications,
      },
    ];

    const filteredItems = allNavigationTabItems.filter(
      i => i && i.key !== null,
    ) as INavigationTabItem[];
    return filteredItems;
  }, [intl, selectedTab]);

  return (
    <PageHeader className="px-2 md:px-0 w-[inherit] md:sticky md:top-0 md:z-10 md:self-start md:pt-5">
      <PageHeader.TopSection>
        <PageHeader.TopSection.Content position={TPageHeaderTopSectionContentPosition.Left}>
          <PageHeader.TopSection.Content.Section deviceMode={TPageHeaderContentDeviceMode.Desktop}>
            <div className="flex items-center">
              {!desktopView && isEditing && (
                <Link href="/account" passHref legacyBehavior>
                  <IconButton
                    tooltip={intl.formatMessage({
                      defaultMessage: 'Back',
                      id: 'cyR7Kh',
                    })}
                    icon={GeneralCaretLeftIcon}
                    variant={EIconButtonVariant.GhostSquareSmall}
                  />
                </Link>
              )}

              <Heading variant={EHeadingStyleVariant.H4} className="py-1 mt-4">
                <FormattedMessage defaultMessage="Account" id="TwyMau" />
              </Heading>
            </div>
          </PageHeader.TopSection.Content.Section>
        </PageHeader.TopSection.Content>
      </PageHeader.TopSection>
      <PageHeader.NavSection
        ariaLabel={intl.formatMessage({
          defaultMessage: 'Account page navigation',
          id: 'nV7Bgu',
        })}>
        {navTabItems.map(i => (
          <PageHeader.NavSection.Item
            key={i.key}
            onClick={() => handleTabSelect(i.key)}
            label={i.label}
            active={i.active}
            disabled={isEditing}
            {...(i.badgeCount && {
              badgeCount: i.badgeCount,
            })}
          />
        ))}
      </PageHeader.NavSection>
    </PageHeader>
  );
};
